import { ComponentProps, useCallback, useEffect, useRef } from 'react';
import { useReactFlow, useStore } from 'reactflow';

import 'reactflow/dist/style.css';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { IconAiGradient } from '@components/icons/ai-gradient';
import { IconHelp } from '@components/icons/help';
import { IconZoomAll } from '@components/icons/zoom-all';
import { IconZoomIn } from '@components/icons/zoom-in';
import { IconZoomOut } from '@components/icons/zoom-out';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { os } from '@utils/operating-system';

import { AiBuilderChat } from './ai-builder/ai-builder-chat';
import {
  AI_BUILDER_CONTROLS_LEFT,
  AI_BUILDER_CONTROLS_SHIFT,
  AI_BUILDER_PANEL_ANIMATION_SECONDS,
  AI_BUILDER_PANEL_WIDTH,
  AI_BUILDER_VIEWPORT_ANIMATION_MS,
  AI_BUILDER_VIEWPORT_SHIFT,
  fitViewWithOffset,
} from './ai-builder/ai-builder-layout';
import { zoomDuration } from './base-builder';

export const Controls = ({
  journeyId,
  isTemplate,
  canSeeAiBuilder,
  isAiBuilderOpen,
  setHasAiBuilderOpen,
}: {
  journeyId?: string;
  isTemplate?: boolean;
  canSeeAiBuilder: boolean;
  isAiBuilderOpen: boolean;
  setHasAiBuilderOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
}) => {
  const { zoomIn, zoomOut, getNodes, getViewport, setViewport, viewportInitialized } =
    useReactFlow();
  const {
    width: viewportWidth,
    height: viewportHeight,
    minZoom,
  } = useStore((state) => ({
    width: state.width,
    height: state.height,
    minZoom: state.minZoom,
  }));
  const { t } = useScopedTranslate(locale);
  const viewportOffsetX = isAiBuilderOpen ? AI_BUILDER_VIEWPORT_SHIFT : 0;
  const previousAiBuilderOpen = useRef<boolean | null>(null);

  useEffect(() => {
    // Shift the viewport when the AI Builder opens/closes so the journey stays centered
    // within the React Flow canvas.
    if (!viewportInitialized) return;

    const previousOffset =
      previousAiBuilderOpen.current === null
        ? 0
        : previousAiBuilderOpen.current
        ? AI_BUILDER_VIEWPORT_SHIFT
        : 0;
    const nextOffset = isAiBuilderOpen ? AI_BUILDER_VIEWPORT_SHIFT : 0;

    if (previousAiBuilderOpen.current !== null || nextOffset !== 0) {
      if (previousOffset !== nextOffset) {
        const viewport = getViewport();
        setViewport(
          { ...viewport, x: viewport.x + (nextOffset - previousOffset) },
          { duration: AI_BUILDER_VIEWPORT_ANIMATION_MS }
        );
      }
    }

    previousAiBuilderOpen.current = isAiBuilderOpen;
  }, [getViewport, isAiBuilderOpen, setViewport, viewportInitialized]);

  const handleFitView = useCallback(() => {
    if (!viewportInitialized) return;

    fitViewWithOffset({
      nodes: getNodes(),
      viewportWidth,
      viewportHeight,
      minZoom,
      maxZoom: 1,
      offsetX: viewportOffsetX,
      duration: zoomDuration,
      setViewport,
    });
  }, [
    getNodes,
    minZoom,
    setViewport,
    viewportHeight,
    viewportInitialized,
    viewportOffsetX,
    viewportWidth,
  ]);

  return (
    <>
      <AnimatePresence initial={false}>
        {isAiBuilderOpen && journeyId && (
          <MotionPanel
            position="top-left"
            style={{ left: 0, top: 0, bottom: 0, width: AI_BUILDER_PANEL_WIDTH }}
            initial={{ x: -AI_BUILDER_PANEL_WIDTH, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -AI_BUILDER_PANEL_WIDTH, opacity: 0 }}
            transition={{
              duration: AI_BUILDER_PANEL_ANIMATION_SECONDS,
              ease: 'easeOut',
            }}
          >
            <AiBuilderChat journeyShortId={journeyId} onClose={() => setHasAiBuilderOpen(false)} />
          </MotionPanel>
        )}
      </AnimatePresence>

      <MotionPanel
        position="bottom-left"
        style={{ left: AI_BUILDER_CONTROLS_LEFT }}
        animate={{ x: isAiBuilderOpen ? AI_BUILDER_CONTROLS_SHIFT : 0 }}
        initial={false}
        transition={{
          duration: AI_BUILDER_PANEL_ANIMATION_SECONDS,
          ease: 'easeOut',
        }}
      >
        <Stack orientation="horizontal" space={2}>
          {!isAiBuilderOpen && canSeeAiBuilder && !isTemplate && journeyId && (
            <Button
              iconLeft={<IconAiGradient />}
              text={t('navigator')}
              variant="ai-gradient"
              onClick={() => setHasAiBuilderOpen(true)}
            />
          )}
          <PopoverButton
            placement="top-start"
            contentStyle={{ maxWidth: pxToRem(294) }}
            trigger={Button}
            triggerProps={{
              iconRight: <IconHelp />,
              variant: 'secondary',
            }}
          >
            <NavigationContainer>
              <Stack space={4}>
                <Text font="semibold14" text={t('navigation')} />
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t('panKey')} />
                  <Text font="regular14" text={t('panLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomScrollKey${os}`) + t('mouseWheel')} />
                  <Text font="regular14" text={t('zoomInOutLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomKey${os}`) + t('plus')} />
                  <Text font="regular14" text={t('zoomInLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomKey${os}`) + t('minus')} />
                  <Text font="regular14" text={t('zoomOutLabel')} />
                </Stack>
                <Stack orientation="horizontal" justify="space-between">
                  <Text font="semibold14" text={t(`zoomKey${os}`) + t('fitViewKey')} />
                  <Text font="regular14" text={t('fitViewLabel')} />
                </Stack>
              </Stack>
            </NavigationContainer>
          </PopoverButton>
          <Button
            iconRight={<IconZoomOut />}
            variant="secondary"
            id="zoom-out"
            onClick={() => zoomOut({ duration: zoomDuration })}
          />
          <Button
            iconRight={<IconZoomIn />}
            variant="secondary"
            id="zoom-in"
            onClick={() => zoomIn({ duration: zoomDuration })}
          />
          <Button iconRight={<IconZoomAll />} variant="secondary" onClick={handleFitView} />
        </Stack>
      </MotionPanel>
    </>
  );
};

const NavigationContainer = styled.div({
  padding: pxToRem(8),
});
type MotionPanelProps = ComponentProps<typeof motion.div> & {
  position: string;
};

const MotionPanel = ({ position, className, style, ...rest }: MotionPanelProps) => {
  // We need animation so we can't use ReactFlow's Panel directly, this mirrors its positioning
  // and pointer-events behavior while using motion.div.
  const pointerEvents = useStore((state) => (state.userSelectionActive ? 'none' : 'all'));
  const positionClasses = position.split('-');

  return (
    <motion.div
      className={['react-flow__panel', className, ...positionClasses].filter(Boolean).join(' ')}
      style={{ ...style, pointerEvents }}
      {...rest}
    />
  );
};
const locale = {
  en: {
    navigation: 'Navigation',
    panKey: 'Space + Drag',
    panLabel: 'Move canvas',
    zoomScrollKeyMacOS: '⌘ + ',
    zoomScrollKeyWindows: 'Ctrl + ',
    zoomScrollKeyLinux: 'Ctrl + ',
    zoomScrollKeyUnknown: 'Ctrl + ',
    zoomKeyMacOS: '⌘ + ',
    zoomKeyWindows: 'Ctrl + ',
    zoomKeyLinux: 'Ctrl + ',
    zoomKeyUnknown: 'Ctrl + ',
    mouseWheel: 'Mousewheel',
    zoomInOutLabel: 'Zoom In / Out',
    plus: 'Plus',
    zoomInLabel: 'Zoom In',
    minus: 'Minus',
    zoomOutLabel: 'Zoom Out',
    fitViewKey: '0 / Home',
    fitViewLabel: 'Fit view',
    navigator: 'Navigator',
  },
};
